import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import authInstance from '../services/httpClient';
import { extractErrorMessage } from '../hooks/queries';

export interface UberFallback {
  eligible: boolean;
  reason: string;
  availableAt: string | null;
}

const WHY: Record<string, string> = {
  loop_gave_up: 'Loop gave up on this order (returned, address or abandoned).',
  no_driver: 'No Loop driver has taken this order.',
};

/**
 * Per-order Uber Direct fallback for Loop orders (backend: deliveries/services/uber-fallback.js).
 * Shown on the order page when Loop gave up on the order or found no driver within
 * System Core's minutes; while Loop still has time it says when the option opens.
 * When Loop gave up, the order can also be sent with Loop again (backend loop-retry).
 * The API allows head office only and refuses anything no longer eligible.
 */
type Courier = 'uber' | 'loop';

const CONFIRM: Record<Courier, { title: string; body: string; yes: string; url: (id: string) => string }> = {
  uber: {
    title: 'Send with Uber Direct?',
    body: 'Any live Loop booking is cancelled first, then Uber Direct is booked for this order. The customer gets Uber’s tracking link.',
    yes: 'Yes, send with Uber',
    url: (id) => `admin/orders/${id}/uber-fallback`,
  },
  loop: {
    title: 'Send with Loop again?',
    body: 'A new Loop booking is made for this order, to the same address. If no driver takes it, the Uber Direct option comes back after System Core’s minutes.',
    yes: 'Yes, send with Loop',
    url: (id) => `admin/orders/${id}/loop-retry`,
  },
};

export default function UberFallbackPanel({ orderId, fallback, loopRetry, onDone }: {
  orderId: string;
  fallback: UberFallback | null | undefined;
  loopRetry?: { eligible: boolean } | null;
  onDone: () => void;
}) {
  const [confirming, setConfirming] = useState<Courier | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!fallback) return null;

  if (!fallback.eligible) {
    if (fallback.reason !== 'waiting_for_driver' || !fallback.availableAt) return null;
    const at = new Date(fallback.availableAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return (
      <Box data-testid="uber-fallback" sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Waiting for a Loop driver. If none takes it, the order can go with Uber Direct from {at}.
        </Typography>
      </Box>
    );
  }

  const canRetryLoop = Boolean(loopRetry?.eligible);

  const send = async () => {
    if (!confirming) return;
    setBusy(true);
    setError(null);
    try {
      await authInstance.post(CONFIRM[confirming].url(orderId));
      setConfirming(null);
      onDone();
    } catch (err) {
      setError(extractErrorMessage(err));
      setConfirming(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box data-testid="uber-fallback" sx={{ mt: 2 }}>
      <Alert
        severity="warning"
        icon={<LocalShippingIcon />}
        action={(
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canRetryLoop && (
              <Button color="inherit" size="small" variant="outlined" onClick={() => setConfirming('loop')} disabled={busy}>
                Send with Loop again
              </Button>
            )}
            <Button color="inherit" size="small" variant="outlined" onClick={() => setConfirming('uber')} disabled={busy}>
              Send with Uber Direct
            </Button>
          </Box>
        )}
      >
        {WHY[fallback.reason] ?? 'Loop can’t deliver this order.'}{' '}
        {canRetryLoop ? 'It can be sent with Loop again, or with Uber Direct instead.' : 'It can be sent with Uber Direct instead.'}
      </Alert>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      <Dialog open={confirming !== null} onClose={() => !busy && setConfirming(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirming && CONFIRM[confirming].title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirming && CONFIRM[confirming].body}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={send} disabled={busy} startIcon={busy ? <CircularProgress size={14} /> : null}>
            {confirming && CONFIRM[confirming].yes}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
